import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ROUTE_BUILDERS } from '@/config/routes'
import { formatAmount } from '@/lib/utils/formatters'
import { PAYMENT_TYPE_LABELS } from '../constants/pages/revenuePaymentLogsPage'
import type { GatewayStatusMetaMap, RevenuePaymentLogRow } from '../types/api'
import type { RevenuePaymentLogsColumnId } from '../types/listColumns'
import { gatewayMetaLookup } from '../services/reportsService'
import { formatReportDate, parseReportDate } from './reportDateDisplay'

function toNumber(value: string | number | undefined): number {
  const n = Number(value ?? 0)
  return Number.isFinite(n) ? n : 0
}

function amountParts(row: RevenuePaymentLogRow) {
  const total = toNumber(row.payment_amount)
  const service = toNumber(row.payment_log_surcharge_fee ?? row.surcharge_fee)
  const refundFee = toNumber(row.payment_log_refund_fee)
  const base =
    service && service < total
      ? total - service
      : total + (refundFee ? refundFee * -1 : 0)
  return { base, service, total }
}

function formatDisplayDate(
  value: string | undefined,
  includeTime = false,
): string {
  if (!value) return '—'
  const d = parseReportDate(value)
  if (!d) return String(value)
  if (includeTime) {
    return d.toLocaleString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  }
  return formatReportDate(value)
}

function padId(value: number | undefined, length: number): string {
  if (value == null) return ''
  return String(value).padStart(length, '0')
}

export type RevenuePaymentLogCellContext = {
  canOpenApplication: boolean
  gatewayMeta: GatewayStatusMetaMap
}

export function renderRevenuePaymentLogCell(
  columnId: RevenuePaymentLogsColumnId,
  row: RevenuePaymentLogRow,
  context: RevenuePaymentLogCellContext,
): ReactNode {
  const amounts = amountParts(row)
  const gateway = gatewayMetaLookup(
    context.gatewayMeta,
    row.payment_source,
    row.gateway_status,
  )

  switch (columnId) {
    case 'payment_log_id':
      return row.payment_log_id ?? '—'
    case 'company_name':
      return row.company_name ?? '—'
    case 'property_name':
      return row.property_name ?? '—'
    case 'applicant_id':
      return row.applicant_id != null && context.canOpenApplication ? (
        <Link
          to={`${ROUTE_BUILDERS.DASHBOARD_APPLICATION_DETAIL(row.applicant_id)}#payment`}
          className="text-[#007BFF] hover:underline"
        >
          {padId(row.applicant_id, 8)}
        </Link>
      ) : (
        padId(row.applicant_id, 8) || '—'
      )
    case 'application_id':
      return padId(row.application_id, 6) || '—'
    case 'first_name':
      return [row.first_name, row.last_name].filter(Boolean).join(' ') || '—'
    case 'special_note':
      return row.special_note === 'COVID_19' ? (
        <span className="mr-1 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
          Covid 19
        </span>
      ) : (
        (row.special_note ?? '—')
      )
    case 'payment_type':
      return row.payment_type || row.type
        ? (PAYMENT_TYPE_LABELS[row.payment_type ?? row.type ?? ''] ??
          row.payment_type ??
          row.type)
        : '—'
    case 'payment_source':
      return row.payment_source ?? '—'
    case 'auth_code':
      return row.auth_code ?? '—'
    case 'payment_amount':
      return formatAmount(amounts.base)
    case 'surcharge_fee':
      return formatAmount(amounts.service)
    case 'total_amount':
      return formatAmount(amounts.total)
    case 'payment_status':
      return row.payment_status ?? '—'
    case 'gateway_status':
      return <span title={gateway.description}>{gateway.display}</span>
    case 'batch_id':
      return row.batch_id ?? '—'
    case 'funding_batch_id':
      return row.funding_batch_id ?? '—'
    case 'funding_master_id':
      return row.funding_master_id ?? '—'
    case 'funding_date':
      return formatDisplayDate(row.funding_date)
    case 'lease_start_date':
      return formatDisplayDate(row.lease_start_date)
    case 'created_at':
      return formatDisplayDate(row.created_at, true)
    default:
      return '—'
  }
}

export function revenuePaymentLogCellClassName(
  columnId: RevenuePaymentLogsColumnId,
): string | undefined {
  switch (columnId) {
    case 'payment_log_id':
      return 'font-mono text-xs tabular-nums'
    case 'auth_code':
      return 'font-mono text-xs'
    case 'payment_amount':
    case 'surcharge_fee':
    case 'total_amount':
      return 'whitespace-nowrap tabular-nums'
    default:
      return undefined
  }
}
